import type React from 'react';

import styles from '@components/tags-component/styles/verktoyTagsComponent.module.scss';
import VerktoyInfoElement from '@components/verktoy-details/components/info/verktoy-info-element';
import { useGetResources } from '@hooks/useGetResources';
import { type Verktoy, VerktoyFormal } from '@src/types/minhelseentities';
import { TagGroup } from '@src/types/types';

import TagList from '@helsenorge/designsystem-react/components/TagList';

import AntattTidTag from './antattTid-tag';
import FagomradeTag from './fagomrade-tag';
import FormalTag from './formal-tag';
import InnholdstypeTag from './innholdstype-tag';
import MalgruppeTag from './malgruppe-tag';
import SprakTag from './sprak-tag';
import VerktoyTypeTag from './verktoyType-tag';

interface Props {
  verktoy: Verktoy;
  tagGroup?: TagGroup;
  excludeLaeringFormaal?: boolean;
}

export const VerktoyTagsComponent = ({ verktoy, tagGroup, excludeLaeringFormaal }: Props): React.JSX.Element | null => {
  const resources = useGetResources();

  const verktoyFormal = verktoy.VerktoyFormal;
  const fagomrader = verktoy.Fagomrader || [];
  const malgruppeTitle = resources?.tagTitle_maalgruppe;
  const verktoyTyper = verktoy.Laeringsressurs.VerktoyTyper || [];
  const malgrupper = verktoy.Malgrupper || [];
  const innholdstyper = verktoy.Laeringsressurs.Innholdstyper || [];
  const antattTid = verktoy.Laeringsressurs.AntattTid;
  const visSprak = verktoy.VisSprak;
  const sprakTyper = verktoy.SprakTyper || [];
  const fagligAnsvarlig = verktoy.FagligAnsvarlig;
  const dataansvarligNavn = verktoy.Laeringsressurs?.DataansvarligNavn;
  const utvikletAv = verktoy.UtvikletAv;
  const shouldRenderFormalTag = !(excludeLaeringFormaal === true && verktoyFormal === VerktoyFormal.Laring);

  switch (tagGroup) {
    case TagGroup.topTags: {
      const topTagComponents = [
        ...(shouldRenderFormalTag ? [<FormalTag key="formal" verktoyFormal={verktoyFormal} />] : []),
        ...fagomrader.map(fagomrade => <FagomradeTag key={fagomrade} fagomrade={fagomrade} />),
      ];

      return topTagComponents.length > 0 ? <TagList>{topTagComponents}</TagList> : null;
    }
    case TagGroup.detailTags:
      return (
        <div className={styles.tagsContainer}>
          <div className={styles.tagContainer}>
            <div className={styles.tagTitle}>{resources?.tagTitle_verktoyType}</div>
            <div className={styles.tagMargin}>
              <TagList>
                {verktoyTyper.map(verktoyType => (
                  <VerktoyTypeTag key={verktoyType} verktoyType={verktoyType} />
                ))}
              </TagList>
            </div>
          </div>

          {!!malgrupper && malgrupper.length > 0 && (
            <div className={styles.tagContainer}>
              <div className={styles.tagTitle}>{malgruppeTitle}</div>
              <div className={styles.tagMargin}>
                <TagList>
                  {malgrupper.map(group => (
                    <MalgruppeTag key={group} malgruppeType={group} />
                  ))}
                </TagList>
              </div>
            </div>
          )}

          <div className={styles.tagContainer}>
            <div className={styles.tagTitle}>{resources?.tagTitle_formaal}</div>
            <div className={styles.tagMargin}>{shouldRenderFormalTag && <FormalTag verktoyFormal={verktoyFormal} />}</div>
          </div>

          {!!innholdstyper && innholdstyper.length > 0 && (
            <div className={styles.tagContainer}>
              <div className={styles.tagTitle}>{resources?.tagTitle_innholdsType}</div>
              {innholdstyper.map(innholdstype => (
                <InnholdstypeTag key={innholdstype} innholdstype={innholdstype} />
              ))}
            </div>
          )}

          <div className={styles.tagContainer}>
            <div className={styles.tagTitle}>{resources?.tagTitle_helseomraade}</div>
            <div className={styles.tagMargin}>
              <TagList>
                {fagomrader.map(fagomrade => (
                  <FagomradeTag key={fagomrade} fagomrade={fagomrade} />
                ))}
              </TagList>
            </div>
          </div>

          {!!antattTid && (
            <div className={styles.tagContainer}>
              <AntattTidTag estimatedTime={antattTid} />
            </div>
          )}

          {!!visSprak && !!sprakTyper && sprakTyper.length > 0 && (
            <div className={styles.tagContainer}>
              <div className={styles.tagTitle}>{resources?.tagTitle_spraak}</div>
              <div className={styles.tagMargin}>
                <TagList>
                  {verktoy.SprakTyper.map(sprakType => (
                    <SprakTag key={sprakType} sprakType={sprakType} />
                  ))}
                </TagList>
              </div>
            </div>
          )}

          {!!fagligAnsvarlig && (
            <div className={styles.tagContainer}>
              <VerktoyInfoElement label={resources?.technicalResponsibleLabel}>
                <div id="fagligAnsvarligId">{fagligAnsvarlig}</div>
              </VerktoyInfoElement>
            </div>
          )}

          {!!dataansvarligNavn && (
            <div className={styles.tagContainer}>
              <VerktoyInfoElement label={resources?.dataansvarligLabel}>
                <div id="dataansvarlig">{dataansvarligNavn}</div>
              </VerktoyInfoElement>
            </div>
          )}
          {!!utvikletAv && (
            <div className={styles.tagContainer}>
              <VerktoyInfoElement label={resources?.developedByLabel || ''}>
                <div id="utvikletAvId">{utvikletAv}</div>
              </VerktoyInfoElement>
            </div>
          )}
        </div>
      );
    default:
      return null;
  }
};

export default VerktoyTagsComponent;
